import React, { useMemo, useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { TrendingUp, WarningAmber } from "@mui/icons-material";

import DefaultButton from "../simple/DefaultButton";

import { BrandContext } from "../../App";
import { callAPI } from "../helpers/CallAPI";

import { lineKeyMap } from "../../consts";

import EngagementAchievementBox from "../simple/EngagementAchievementBox";
import SkillsChartFilter from "./SkillsChartFilter";

// --- Helper Functions ---

// 1. Format strings like "action_setting_score" to "Action Setting"
const formatSkillName = (key) => {
  return key
    .replace("_score", "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// 2. Generate a consistent color for each skill string
const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "00000".substring(0, 6 - c.length) + c;
};

const TeamSkillChart = () => {
  const { brand, user, authToken } = React.useContext(BrandContext);
  const teamId = user?.teams?.length ? user?.teams[0]?.id : null;
  const theme = useTheme();
  const [rawData, setRawData] = useState([]);
  const [skillFilter, setSkillFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [days, setDays] = useState(7);
  const [customInput, setCustomInput] = useState("");
  const [customInputError, setCustomInputError] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const minCustomInput = 1;
  const maxCustomInput = 120;

  console.log("user", user);

  useEffect(() => {
    loadScores();
  }, []);

  const handleViewChange = (_, newValue) => {
    if (newValue !== null) {
      setDays(newValue);
    }
  };

  const loadScores = async () => {
    // Start with the base URL
    if (user && user?.teams?.length) {
      let endpoint = `scores/team/${
        teamFilter !== "all" ? teamFilter : user?.teams[0]?.id
      }`;

      // Check if 'days' has a value and append it as a query parameter
      if (days) {
        endpoint += `?days=${days}`;
      }

      const data = await callAPI(endpoint, {
        token: authToken,
      });
      setRawData(data);
    }
  };

  useEffect(() => {
    loadScores();
  }, [days, teamFilter]);

  useEffect(() => {
    fetch(`${brand.api_base_url}/team_users?team_id=${teamId}`, {
      headers: { Authorization: `Token ${authToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch team users");
        return res.json();
      })
      .then((data) => {
        setIsManager(true);
      })
      .catch((err) => console.error(err));
  }, [teamId]);

  const handleExport = () => {
    if (!rawData || !rawData.scores) return;

    const scoresObj = rawData.scores;

    // Sort Date Descending (Newest first) for the CSV
    const dates = Object.keys(scoresObj).sort(
      (a, b) => new Date(b) - new Date(a)
    );

    // 1. Get all unique skill keys dynamically
    const allSkillKeys = new Set();
    dates.forEach((date) => {
      Object.keys(scoresObj[date]).forEach((key) => {
        if (key !== "total_score" && key.endsWith("_score")) {
          allSkillKeys.add(key);
        }
      });
    });
    const skillsArray = Array.from(allSkillKeys);

    // 2. Build CSV Headers
    // Result: "Date,Action Setting,Reviewing Actions,...,Team Average"
    const headers = [
      "Date",
      ...skillsArray.map(formatSkillName),
      "Team Average",
    ];

    // 3. Build CSV Rows
    const rows = dates.map((date) => {
      let dailySum = 0;
      let dailyCount = 0;

      // Map over every known skill to ensure columns align correctly
      const skillValues = skillsArray.map((skill) => {
        const val = scoresObj[date][skill]?.average;

        // Track stats for the Average calculation
        if (val !== undefined && val !== null) {
          dailySum += val;
          dailyCount++;
          return val;
        }
        return ""; // Leave cell blank if no score for this skill on this date
      });

      // Calculate Average (matching your Chart logic)
      const apiTotal = scoresObj[date]?.total_score?.average || 0;
      const calculatedAvg = dailyCount > 0 ? dailySum / dailyCount : 0;
      const finalAvg = apiTotal > 0 ? apiTotal : calculatedAvg;

      return [date, ...skillValues, finalAvg.toFixed(2)].join(",");
    });

    // 4. Combine and Download
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `team_scores_export_${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- Data Processing & Metrics Calculation ---
  const { chartData, availableSkills, metrics, skillAverages } = useMemo(() => {
    if (!rawData || !rawData.scores)
      return {
        chartData: [],
        availableSkills: [],
        metrics: {},
        skillAverages: {},
      };

    const scoresObj = rawData.scores;
    const dates = Object.keys(scoresObj).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    const allSkillKeys = new Set();
    dates.forEach((date) => {
      Object.keys(scoresObj[date]).forEach((key) => {
        if (key !== "total_score" && key.endsWith("_score")) {
          allSkillKeys.add(key);
        }
      });
    });
    const skillsArray = Array.from(allSkillKeys);

    // --- NEW: Calculate Overall Averages for the Legend ---
    const averages = {};
    skillsArray.forEach((skill) => {
      let sum = 0;
      let count = 0;

      dates.forEach((date) => {
        const val = scoresObj[date][skill]?.average;
        if (val !== undefined && val !== null) {
          sum += val;
          count++;
        }
      });

      // Calculate average, default to 0 if no data
      averages[skill] = count > 0 ? (sum / count).toFixed(1) : 0;
    });

    // --- Build Chart Data & Calculate Team Average ---
    const processedData = dates.map((date) => {
      const entry = { date };

      let dailySum = 0;
      let dailyCount = 0;

      skillsArray.forEach((skill) => {
        const skillData = scoresObj[date][skill];
        const val = skillData ? skillData.average : null;

        entry[skill] = val;

        // Calculate manual average for the tooltip
        if (val !== null) {
          dailySum += val;
          dailyCount++;
        }
      });

      // Prefer API total_score, fallback to manual calculation if 0 (based on your json)
      const apiTotal = scoresObj[date]?.total_score?.average || 0;
      entry.teamAverage =
        apiTotal > 0 ? apiTotal : dailyCount > 0 ? dailySum / dailyCount : 0;

      return entry;
    });

    // 3. Calculate Metrics
    let highestImproving = { name: "N/A", value: 0 };
    let needsAttention = { name: "N/A", value: 100 };

    let bestImprovementDelta = -Infinity;
    let lowestCurrentScore = Infinity;

    skillsArray.forEach((skill) => {
      const firstEntry = processedData.find((d) => d[skill] !== null);
      const lastEntry = [...processedData]
        .reverse()
        .find((d) => d[skill] !== null);

      if (firstEntry && lastEntry) {
        const startVal = firstEntry[skill];
        const endVal = lastEntry[skill];
        const delta = endVal - startVal;

        if (delta > bestImprovementDelta) {
          bestImprovementDelta = delta;
          highestImproving = {
            name: formatSkillName(skill),
            value: `+${delta.toFixed(1)}`,
          };
        }
      }

      if (lastEntry) {
        const currentVal = lastEntry[skill];
        if (currentVal < lowestCurrentScore) {
          lowestCurrentScore = currentVal;
          needsAttention = {
            name: formatSkillName(skill),
            value: currentVal.toFixed(1),
          };
        }
      }
    });

    return {
      chartData: processedData,
      availableSkills: skillsArray,
      metrics: { highestImproving, needsAttention },
      skillAverages: averages,
    };
  }, [rawData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // payload[0].payload gives us the full data object for this date
      const data = payload[0].payload;

      return (
        <Card sx={{ p: 2, border: "1px solid #ccc", boxShadow: 3 }}>
          <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{
                display: "block",
                color: "#181D27",
                fontSize: "12px",
              }}
            >
              Team Average Score: {data.teamAverage.toFixed(1)}
            </Typography>
            <Typography
              sx={{
                color: "#717680",
                fontSize: "12px",
              }}
            >
              {new Date(label).toLocaleDateString()}
            </Typography>
          </Box>
        </Card>
      );
    }

    return null;
  };

  console.log("team filter", teamFilter);

  // --- Render ---
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: theme.containers.paddingMobile,
        flexGrow: 1,
        color: theme.palette.text.primary,
        backgroundColor: "transparent",
        border: `2px solid`,
        borderColor: theme.palette.border.tertiary,
        borderRadius: theme.shape.radius,
        boxShadow:
          "0px 12px 16px -4px rgba(0, 91, 140, 0.10), 0px 4px 6px -2px rgba(0, 91, 140, 0.03), 0px 2px 2px -1px rgba(0, 91, 140, 0.04)",
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: theme.typography.textMD.fontSize,
            lineHeight: theme.typography.textMD.lineHeight,
            fontWeight: theme.typography.fontWeightMedium,
            marginBottom: "12px",
          }}
        >
          Team Skill Progress
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: "1.3",
            fontWeight: theme.typography.fontWeightLight,
            fontStyle: "italic",
            mb: theme.customSpacing["2xl"],
          }}
        >
          Track how your team is developing across key program skills. View team
          trends!
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          mb: theme.customSpacing.xl,
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: "10px",
          },
        }}
      >
        <Box
          sx={{
            mr: theme.customSpacing["8xl"],
            [theme.breakpoints.down("md")]: {
              mr: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Typography
              sx={{
                fontSize: theme.typography.textXS.fontSize,
                lineHeight: theme.typography.textXS.lineHeight,
                fontWeight: theme.typography.fontWeightLight,
                fontStyle: "italic",
                mb: theme.customSpacing.sm,
              }}
            >
              View:
            </Typography>
            <Box sx={{ flexGrow: 1 }}></Box>
            {/* <Typography
              sx={{
                fontSize: theme.typography.textXS.fontSize,
                lineHeight: theme.typography.textXS.lineHeight,
                fontWeight: theme.typography.fontWeightLight,
                fontStyle: "italic",
                textAlign: "left",
                width: 100,
                mb: theme.customSpacing.sm,
                marginRight: theme.customSpacing.lg,
              }}
            >
              Custom range:
            </Typography> */}
          </Box>

          <ToggleButtonGroup
            value={days}
            exclusive
            onChange={handleViewChange}
            sx={{
              backgroundColor: "transparent",
              height: 43,
              [theme.breakpoints.down("md")]: {
                justifyContent: "space-between",
                width: "100%",
              },
              "& .MuiToggleButton-root": {
                color: theme.palette.foreground.button_secondary,
                textTransform: "none",
                border: "none",
                borderRadius: 2,
                px: theme.customSpacing.lg,
                py: theme.customSpacing.md,
                fontWeight: theme.typography.fontWeightMedium,
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                  color: theme.palette.foreground.badge_icon,
                  border: "1px solid",
                  borderColor: theme.palette.border.tertiary,
                },
                "&:hover": {
                  backgroundColor: "rgba(154, 92, 255, 0.05)",
                },
              },
            }}
          >
            <div>
              <ToggleButton value={7}>Weekly</ToggleButton>
              <ToggleButton value={31}>Monthly</ToggleButton>
            </div>
            {/* <TextField
              type="number"
              placeholder={`${minCustomInput}-${maxCustomInput} days`}
              variant="outlined"
              size="medium"
              error={customInputError}
              helperText={
                customInputError
                  ? `${minCustomInput}-${maxCustomInput} days`
                  : ""
              }
              FormHelperTextProps={{
                sx: {
                  fontSize: theme.typography.textXS.fontSize,
                  color: theme.palette.text.error_primary,
                  overflow: "",
                },
              }}
              onInput={(e) => {
                const val = Number(e.target.value);
                if (val >= minCustomInput && val <= maxCustomInput) {
                  setDays(val);
                  setCustomInputError(false);
                } else {
                  setCustomInputError(true);
                }
              }}
              inputProps={{
                min: minCustomInput,
                max: maxCustomInput,
                style: {
                  textAlign: "center",
                },
              }}
              sx={{
                mx: theme.customSpacing.lg,
                my: theme.customSpacing.md,
                width: 100,
                input: {
                  color: theme.palette.foreground.button_secondary,
                  fontSize: theme.typography.textXS.fontSize,
                  padding: theme.customSpacing.xs,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.tertiary,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.tertiary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.brand,
                },
              }}
            /> */}
          </ToggleButtonGroup>
        </Box>
        <SkillsChartFilter
          teams={user?.teams}
          isManager={isManager}
          skills={availableSkills}
          teamFilter={teamFilter}
          skillFilter={skillFilter}
          setSkillFilter={setSkillFilter}
          setTeamFilter={setTeamFilter}
        />
      </Box>

      <Box
        className="key-explanation"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: "30px",
          marginBottom: "20px",
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
          },
          "& > p": {
            fontSize: "14px",
            lineHeight: "1.3",
            margin: 0,
          },
          "& .key": {
            "& ul": {
              padding: 0,
              listStyle: "none",
              margin: 0,
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",

              "& li": {
                display: "flex",
                gap: "10px",
                fontWeight: "400",
                fontSize: "14px",
                alignItems: "center",
                color: "#D5D7DA",
                lineHeight: "1.2",
                "& span": {
                  display: "inline-block",
                  background: "#fff",
                  borderRadius: "50%",
                  width: "8px",
                  height: "8px",
                },
                "&.os": {
                  "& span": {
                    background: "#962EFF",
                  },
                },
                "&.is": {
                  "& span": {
                    background: "#00B2D6",
                  },
                },
                "&.eq": {
                  "& span": {
                    background: "#D1C7FF",
                  },
                },
                "&.ifs": {
                  "& span": {
                    background: "#D1C7FF",
                  },
                },
              },
            },
          },
        }}
      >
        <div className="key">
          <ul>
            {availableSkills.map((item, i) => {
              if (item.key === "total_score") return;
              return (
                <li key={i} className={item.key}>
                  <span
                    style={{
                      backgroundColor: lineKeyMap[item]?.color,
                    }}
                  ></span>{" "}
                  {formatSkillName(item)} | Team Avg: {skillAverages[item]}
                </li>
              );
            })}
          </ul>
        </div>
      </Box>

      {/* Recharts Graph */}
      <Box sx={{ width: "100%", height: 350, mb: 4 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ bottom: 40 }}>
            <CartesianGrid
              stroke={theme.palette.border.tertiary}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{
                fill: theme.palette.text.tertiary,
                style: {
                  fontSize: theme.typography.textXS.fontSize,
                },
                dy: 5,
              }}
              label={{
                value: "DATE",
                dy: 30,
                fill: theme.palette.text.tertiary,
                fontSize: theme.typography.textXS.fontSize,
                fontWeight: theme.typography.fontWeightRegular,
              }}
              tickFormatter={(date) => {
                const d = new Date(date);
                return `${String(d.getDate()).padStart(2, "0")}/${String(
                  d.getMonth() + 1
                ).padStart(2, "0")}/${d.getFullYear()}`;
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              label={{
                value: "SCORE",
                angle: -90,
                position: "insideLeft",
                fill: theme.palette.text.tertiary,
                fontSize: theme.typography.textXS.fontSize,
                fontWeight: theme.typography.fontWeightRegular,
                style: { textAnchor: "middle" },
              }}
              tickFormatter={(value) => value.toFixed(0)}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* <Legend
              wrapperStyle={{ paddingTop: "20px" }}
              formatter={formatSkillName}
            /> */}

            {/* Render Lines based on Filter */}
            {availableSkills.map((skill) => {
              // If filter is 'all', show everything. If filter is specific, show only that.
              if (skillFilter !== "all" && skillFilter !== skill) return null;

              return (
                <Line
                  key={skill}
                  type="monotone"
                  dataKey={skill}
                  stroke={lineKeyMap[skill]?.color} // alternating colors
                  strokeWidth={2}
                  dot={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <Box
        id="metrics-container"
        sx={{
          display: "flex",
          gap: theme.customSpacing["6xl"],
          mb: 5,
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: "20px",
          },
        }}
      >
        {metrics?.highestImproving && (
          <EngagementAchievementBox
            title="Highest Improving Skill"
            icon={
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.6667 4.66675L9.42091 9.9125C9.1569 10.1765 9.02489 10.3085 8.87267 10.358C8.73878 10.4015 8.59455 10.4015 8.46065 10.358C8.30843 10.3085 8.17643 10.1765 7.91241 9.9125L6.08757 8.08766C5.82356 7.82365 5.69156 7.69164 5.53934 7.64219C5.40544 7.59868 5.26121 7.59868 5.12732 7.64219C4.9751 7.69164 4.84309 7.82365 4.57908 8.08766L1.33333 11.3334M14.6667 4.66675H9.99999M14.6667 4.66675V9.33341"
                  stroke="#FEFEFE"
                  stroke-width="1.39167"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            }
            content={
              <>
                {metrics.highestImproving?.name} :{" "}
                <span style={{ color: "#079455" }}>
                  {metrics.highestImproving?.value} pts
                </span>
              </>
            }
          />
        )}
        {metrics?.needsAttention && (
          <EngagementAchievementBox
            title="Skill Needing Attention"
            icon={
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_12_23187)">
                  <path
                    d="M12 1.33325L12.4119 2.15697C12.5889 2.51096 12.6774 2.68795 12.7956 2.84133C12.9005 2.97743 13.0225 3.09943 13.1586 3.20434C13.312 3.32257 13.489 3.41106 13.843 3.58806L14.6667 3.99992L13.843 4.41178C13.489 4.58877 13.312 4.67727 13.1586 4.79549C13.0225 4.9004 12.9005 5.02241 12.7956 5.15851C12.6774 5.31188 12.5889 5.48888 12.4119 5.84286L12 6.66658L11.5881 5.84286C11.4112 5.48888 11.3227 5.31188 11.2044 5.15851C11.0995 5.02241 10.9775 4.9004 10.8414 4.79549C10.688 4.67727 10.5111 4.58877 10.1571 4.41178L9.33334 3.99992L10.1571 3.58806C10.5111 3.41106 10.688 3.32257 10.8414 3.20434C10.9775 3.09943 11.0995 2.97742 11.2044 2.84133C11.3227 2.68795 11.4112 2.51096 11.5881 2.15697L12 1.33325Z"
                    stroke="#FEFEFE"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M14 8.92612C13.126 10.4593 11.4764 11.4929 9.58531 11.4929C6.78073 11.4929 4.50718 9.21937 4.50718 6.4148C4.50718 4.52361 5.541 2.87388 7.07434 1.99992C3.85321 2.30533 1.33334 5.01787 1.33334 8.31894C1.33334 11.8246 4.17529 14.6666 7.68101 14.6666C10.9819 14.6666 13.6944 12.147 14 8.92612Z"
                    stroke="#FEFEFE"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_12_23187">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
            content={
              <>
                {metrics.needsAttention?.name} :{" "}
                <span style={{ color: "#D92D20" }}>
                  {metrics.needsAttention?.value || "0"}
                </span>
              </>
            }
          />
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <DefaultButton
          onClick={() => handleExport()}
          endIcon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.5 12.5V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V12.5M14.1667 8.33333L10 12.5M10 12.5L5.83333 8.33333M10 12.5V2.5"
                stroke="#030039"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          }
        >
          Download CSV
        </DefaultButton>
      </Box>
    </Card>
  );
};

export default TeamSkillChart;
